import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { Button } from "../../../../../components/button";
import { translateMessage } from "@delibrandev/utils";
import { setAlert } from "../../../../../reducers/userConfigSlice";
import {
  createDomain,
  getDomainCloudflareHostname,
  getCorporationDomains,
  deleteDomainCloudflareHostname,
} from "../../../../../api/clients";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  bgcolor: "background.paper",
  border: "0px",
  borderRadius: "10px",
  boxShadow: 24,
  p: 2,
};

/**
 * Botonera por plataforma:
 * 1) Crear dominio (POST)
 * 2) Ver configuración (GET Cloudflare hostname)
 * 3) Refrescar dominios (GET dominios corporation)
 * 4) Eliminar dominio (DELETE dominio + custom hostname)
 *
 * Props:
 * - platformType: "SHOP" | "DELIVERY" | "TPV" | "KITCHEN"
 * - domainIdFromList: domainId obtenido desde el listado de la corporación (Settings)
 * - onDomainsUpdated: callback opcional para sincronizar Settings tras refresh/create/delete
 */
export const DomainActions = ({ platformType, domainIdFromList, onDomainsUpdated }) => {
  const dispatch = useDispatch();
  const corporationId = useSelector((state) => state.aplicationConfig.userConfig.floatPanel.data.corporationId);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const [domainInput, setDomainInput] = useState("");
  const [domainIdInput, setDomainIdInput] = useState("");

  const [lastDomainId, setLastDomainId] = useState(null);
  const [info, setInfo] = useState(null);

  const effectiveDomainId = useMemo(() => {
    // prioridad: último creado -> domainIdFromList -> input manual
    return lastDomainId || domainIdFromList || domainIdInput || "";
  }, [lastDomainId, domainIdFromList, domainIdInput]);

  const closeCreate = () => setIsCreateOpen(false);

  const closeInfo = () => {
    setIsInfoOpen(false);
    setInfo(null);
  };

  const refreshDomains = async () => {
    if (!corporationId) {
      dispatch(setAlert(translateMessage("Missing required params")));
      return;
    }

    const response = await getCorporationDomains(corporationId);

    if (!response) {
      dispatch(setAlert(translateMessage("Bad request")));
      return;
    }

    dispatch(setAlert(translateMessage(response?.message || "Information retrieved successfully")));
    onDomainsUpdated?.(response);
  };

  const handleCreateDomain = async () => {
    if (!corporationId) {
      dispatch(setAlert(translateMessage("Missing required params")));
      return;
    }

    if (!domainInput || !platformType) {
      dispatch(setAlert(translateMessage("Missing required params")));
      return;
    }

    const response = await createDomain(corporationId, {
      domain: domainInput.trim(),
      platformType,
    });

    // patrón que pediste para POST
    dispatch(setAlert(translateMessage(response?.message || "Bad request")));

    if (response && response.error === false && response?.data?.domainId) {
      setLastDomainId(response.data.domainId);
      setDomainIdInput(response.data.domainId);
      closeCreate();

      // 🔁 refrescar listado de dominios tras crear
      await refreshDomains();
    }
  };

  const handleGetDomainInfo = async () => {
    const domainId = (effectiveDomainId || "").trim();

    if (!domainId) {
      dispatch(setAlert(translateMessage("Missing required params: domainId")));
      return;
    }

    const response = await getDomainCloudflareHostname(domainId);

    if (!response) {
      dispatch(setAlert(translateMessage("Bad request")));
      return;
    }

    if (response.error) {
      dispatch(setAlert(translateMessage(response?.message || "Bad request")));
      return;
    }

    setInfo(response);
    setIsInfoOpen(true);
  };

  const handleDeleteDomain = async () => {
    const domainId = (effectiveDomainId || "").trim();

    if (!domainId) {
      dispatch(setAlert(translateMessage("Missing required params: domainId")));
      return;
    }

    const ok = window.confirm(`¿Seguro que quieres eliminar el dominio (${domainId}) y su hostname en Cloudflare?`);
    if (!ok) return;

    const response = await deleteDomainCloudflareHostname(domainId);

    if (!response) {
      dispatch(setAlert(translateMessage("Bad request")));
      return;
    }

    dispatch(setAlert(translateMessage(response?.message || "Bad request")));

    if (response.error === false) {
      // limpiamos cache local y refrescamos listado
      setLastDomainId(null);
      setDomainIdInput("");
      await refreshDomains();
    }
  };

  return (
    <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "8px", flexWrap: "wrap" }}>
      <Button text="Añadir dominio propio" customClass="customButtonColor1" action={() => setIsCreateOpen(true)} />
      <Button disabled={!effectiveDomainId} text="Ver configuración DNS" customClass="customButtonColor2" action={handleGetDomainInfo} />
      <Button text="Actualizar dominios" customClass="customButtonColor2" action={refreshDomains} />
      <Button disabled={!effectiveDomainId} text="Eliminar dominio propio" customClass="customButtonColor2" action={handleDeleteDomain} />

      {/* CREATE DOMAIN MODAL */}
      <Modal open={isCreateOpen} onClose={closeCreate} aria-labelledby="create-domain-title">
        <Box sx={modalStyle}>
          <Typography id="create-domain-title" variant="h6" component="h2" style={{ marginBottom: "12px" }}>
            Crear dominio ({platformType})
          </Typography>

          <TextField
            fullWidth
            label="Dominio"
            placeholder="ejemplo.net"
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            size="small"
            style={{ marginBottom: "12px" }}
          />

          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <Button text="Cancelar" customClass="customButtonColor2" action={closeCreate} />
            <Button text="Crear" customClass="customButtonColor1" action={handleCreateDomain} />
          </div>
        </Box>
      </Modal>

      {/* INFO MODAL */}
      <Modal open={isInfoOpen} onClose={closeInfo} aria-labelledby="domain-info-title">
        <Box sx={modalStyle}>
          <Typography id="domain-info-title" variant="h6" component="h2" style={{ marginBottom: "12px" }}>
            Dominio y configuración Cloudflare
          </Typography>

          <TextField
            fullWidth
            label="DomainId"
            value={effectiveDomainId}
            onChange={(e) => setDomainIdInput(e.target.value)}
            size="small"
            style={{ marginBottom: "12px" }}
          />

          <div
            style={{
              maxHeight: "50vh",
              overflow: "auto",
              padding: "12px",
              borderRadius: "8px",
              border: "1px solid rgba(0,0,0,0.12)",
              marginBottom: "12px",
            }}
          >
            <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {info ? JSON.stringify(info, null, 2) : ""}
            </pre>
          </div>

          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <Button text="Cerrar" customClass="customButtonColor2" action={closeInfo} />
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default DomainActions;
